import { utc } from '@date-fns/utc'
import {
  addDays,
  addMinutes,
  endOfDay,
  format,
  isSameDay,
  max,
  parse,
  roundToNearestMinutes,
  setHours,
  startOfDay,
} from 'date-fns'
import {
  OperatingHoursBar,
  OperatingHoursBarSegment,
  OperatingHoursRange,
  ProcessSegmentsOptions,
  RawOperatingHoursBar,
  ServiceOperatingHoursDataV1,
  ServiceOperatingHoursDataV2,
} from './types'
import { cloneDeep, isEqual, omit, orderBy } from 'lodash'
import i18n from 'i18n'

export const DATE_FORMATS = {
  WEEK_FORMAT: 'eeee',
  DAY_FORMAT: 'yyyy-MM-dd',
  UTC_PARSE_FORMAT: 'eeee HH:mm:ss',
  TIME_FORMAT: 'hh:mmaaa',
} as const

export const OPERATING_HOURS_WEEK_STARTS_ON = 1

export const createWeekdayTimeUtcDate = (weekday: string, time: string, baseDate: Date) =>
  new Date(parse(`${weekday} ${time}`, DATE_FORMATS.UTC_PARSE_FORMAT, baseDate, { in: utc }))

export const getTimezoneSafeWeekDates = (baseDate: Date) => {
  const nextDays = Array.from({ length: 8 }).map((_, i) => setHours(addDays(new Date(baseDate), i), 12))

  return [
    setHours(addDays(new Date(baseDate), -1), 12), // Yesterday
    ...nextDays, // Today + next 7 days
  ]
}

export const mergeOverlappingRanges = (timeRanges: OperatingHoursRange[]): OperatingHoursRange[] => {
  const sorted = cloneDeep(timeRanges).sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())

  const merged: OperatingHoursRange[] = []

  for (const current of sorted) {
    const last = merged[merged.length - 1]

    if (last && current.startsAt <= last.endsAt) {
      last.endsAt = max([last.endsAt, current.endsAt])
      last.durationMs = last.endsAt.getTime() - last.startsAt.getTime()
    } else {
      merged.push(current)
    }
  }

  return merged
}

export const processOperatingHoursToRanges = (
  serviceOperatingHoursData: ServiceOperatingHoursDataV1 | ServiceOperatingHoursDataV2 | null,
  baseDate: Date,
) => {
  if (!serviceOperatingHoursData) return []

  // Handle v2 operating hours data here
  if (serviceOperatingHoursData.version === 2) {
    const { configs, isEmergencyOverride } = serviceOperatingHoursData
    if (!configs?.length || isEmergencyOverride) return []

    const timezoneSafeWeekDates = getTimezoneSafeWeekDates(baseDate)
    const timeRanges: OperatingHoursRange[] = []

    for (const config of configs) {
      if (!config?.days?.length) return

      // Validate the config window covers the current day
      const configStart = startOfDay(parse(config.startDate, DATE_FORMATS.DAY_FORMAT, new Date(baseDate)))
      const configEnd = endOfDay(parse(config.endDate, DATE_FORMATS.DAY_FORMAT, new Date(baseDate)))

      timezoneSafeWeekDates.forEach((weekdayDate) => {
        const startOfCurrentDay = startOfDay(new Date(weekdayDate))
        const endOfCurrentDay = endOfDay(new Date(weekdayDate))

        // Only apply config if it fully covers the current day (same rule as v1)
        const isWithinConfigWindow =
          configStart.valueOf() <= startOfCurrentDay.valueOf() && configEnd.valueOf() >= endOfCurrentDay.valueOf()
        if (!isWithinConfigWindow) return

        const weekday = format(weekdayDate, DATE_FORMATS.WEEK_FORMAT)

        const dayRules = config.days?.filter((d) => d.weekday === weekday) || []
        dayRules.forEach(({ openTime, closeTime }) => {
          if (openTime && closeTime) {
            // Treat cases where closeTime is earlier than or equal to openTime as next-day close
            const isOvernight = closeTime <= openTime

            // Add 30 minutes buffer to the start time (UTC)
            const openTimeDate = addMinutes(createWeekdayTimeUtcDate(weekday, openTime, weekdayDate), -30)
            let closeTimeDate = createWeekdayTimeUtcDate(weekday, closeTime, weekdayDate)
            if (isOvernight) {
              closeTimeDate = addDays(closeTimeDate, 1)
            }

            if (isSameDay(openTimeDate, closeTimeDate)) {
              timeRanges.push({
                startsAt: openTimeDate,
                endsAt: closeTimeDate,
                durationMs: closeTimeDate.valueOf() - openTimeDate.valueOf(),
                weekday,
              })
            } else {
              const endOfStartDay = endOfDay(openTimeDate)
              const startOfNextDay = startOfDay(closeTimeDate)

              timeRanges.push({
                startsAt: openTimeDate,
                endsAt: endOfStartDay,
                durationMs: endOfStartDay.valueOf() - openTimeDate.valueOf(),
                weekday: format(openTimeDate, DATE_FORMATS.WEEK_FORMAT),
              })

              timeRanges.push({
                startsAt: startOfNextDay,
                endsAt: closeTimeDate,
                durationMs: closeTimeDate.valueOf() - startOfNextDay.valueOf(),
                weekday: format(closeTimeDate, DATE_FORMATS.WEEK_FORMAT),
              })
            }
          }
        })
      })
    }

    return mergeOverlappingRanges(orderBy(timeRanges, [(i) => i.startsAt.valueOf()], ['asc']))
  }

  const { operatingHours } = serviceOperatingHoursData

  if (!operatingHours?.length) return []

  const timezoneSafeWeekDates = getTimezoneSafeWeekDates(baseDate)
  const timeRanges: OperatingHoursRange[] = []

  timezoneSafeWeekDates.forEach((weekdayDate) => {
    const formattedDate = format(weekdayDate, DATE_FORMATS.DAY_FORMAT)
    const weekday = format(weekdayDate, DATE_FORMATS.WEEK_FORMAT)

    const precedentsData = operatingHours.filter((i) => i.date === formattedDate)

    if (precedentsData?.some((i) => i.isClosed)) return

    const startOfCurrentDay = startOfDay(new Date(weekdayDate))
    const endOfCurrentDay = endOfDay(new Date(weekdayDate))

    const dataForWeekday = precedentsData?.length
      ? precedentsData
      : operatingHours?.filter((i) => {
          if (i.weekday !== weekday || !i.startDate || !i.endDate) return false

          const startDateObj = startOfDay(parse(i.startDate, DATE_FORMATS.DAY_FORMAT, new Date(baseDate)))
          const endDateObj = endOfDay(parse(i.endDate, DATE_FORMATS.DAY_FORMAT, new Date(baseDate)))

          return (
            startDateObj.valueOf() <= startOfCurrentDay.valueOf() && endDateObj.valueOf() >= endOfCurrentDay.valueOf()
          )
        })

    dataForWeekday.forEach(({ openTime, closeTime }) => {
      if (openTime && closeTime) {
        // Add 30 minutes buffer to the start time
        const openTimeDate = addMinutes(createWeekdayTimeUtcDate(weekday, openTime, weekdayDate), -30)
        const closeTimeDate = createWeekdayTimeUtcDate(weekday, closeTime, weekdayDate)

        if (isSameDay(openTimeDate, closeTimeDate)) {
          timeRanges.push({
            startsAt: openTimeDate,
            endsAt: closeTimeDate,
            durationMs: closeTimeDate.valueOf() - openTimeDate.valueOf(),
            weekday,
          })
        } else {
          const endOfStartDay = endOfDay(openTimeDate)
          const startOfNextDay = startOfDay(closeTimeDate)

          timeRanges.push({
            startsAt: openTimeDate,
            endsAt: endOfStartDay,
            durationMs: endOfStartDay.valueOf() - openTimeDate.valueOf(),
            weekday: format(openTimeDate, DATE_FORMATS.WEEK_FORMAT),
          })

          timeRanges.push({
            startsAt: startOfNextDay,
            endsAt: closeTimeDate,
            durationMs: closeTimeDate.valueOf() - startOfNextDay.valueOf(),
            weekday: format(closeTimeDate, DATE_FORMATS.WEEK_FORMAT),
          })
        }
      }
    })
  })

  return mergeOverlappingRanges(timeRanges)
}

export const checkIsInOperatingHoursRange = (timeRanges: OperatingHoursRange[], baseDate: Date) => {
  if (!timeRanges?.length) return false

  const now = new Date(baseDate)
  return timeRanges.some((r) => now >= r.startsAt && now <= r.endsAt)
}

const getSegmentWithoutDates = (segment: OperatingHoursBarSegment) => omit(segment, ['startsAt', 'endsAt'])

export const processTimeRangesToWeekdaysWithSegments = (
  timeRanges: OperatingHoursRange[],
  options: ProcessSegmentsOptions,
): RawOperatingHoursBar[] => {
  if (!timeRanges?.length) return []

  const { initialDate, highlightedDate, excludeAvailable, excludeUnavailable } = options || {}

  const baseDate = new Date(initialDate || Date.now())

  const weekdaysDates = Array.from({ length: 7 }).map((_, i) => addDays(new Date(baseDate), i))

  return weekdaysDates.map((weekdayDate) => {
    const dayStart = startOfDay(weekdayDate).valueOf()
    const dayEnd = endOfDay(weekdayDate).valueOf()
    const totalDuration = dayEnd - dayStart

    const weekday = format(weekdayDate, DATE_FORMATS.WEEK_FORMAT)

    const daySegments = timeRanges
      .filter((segment) => segment.weekday === weekday)
      .map((segment) => {
        const start = segment.startsAt.valueOf()
        const end = segment.endsAt.valueOf()
        const leftPositionPercentage = ((start - dayStart) / totalDuration) * 100
        const widthPercentage = ((end - start) / totalDuration) * 100

        return {
          leftPositionPercentage,
          widthPercentage,
          isAvailable: false,
          startsAt: segment.startsAt,
          endsAt: segment.endsAt,
        }
      })
      .filter((seg) => seg.leftPositionPercentage < 100)
      .sort((a, b) => a.leftPositionPercentage - b.leftPositionPercentage)

    let segments: OperatingHoursBarSegment[] = []
    let prevEnd = dayStart

    for (const seg of daySegments) {
      const segStart = seg.startsAt!.valueOf()
      if (segStart > prevEnd) {
        segments.push({
          leftPositionPercentage: ((prevEnd - dayStart) / totalDuration) * 100,
          widthPercentage: ((segStart - prevEnd) / totalDuration) * 100,
          isAvailable: true,
          startsAt: new Date(prevEnd),
          endsAt: new Date(segStart),
        })
      }
      segments.push(seg)
      prevEnd = Math.max(prevEnd, seg.endsAt!.valueOf())
    }

    if (prevEnd < dayEnd) {
      segments.push({
        leftPositionPercentage: ((prevEnd - dayStart) / totalDuration) * 100,
        widthPercentage: ((dayEnd - prevEnd) / totalDuration) * 100,
        isAvailable: true,
        startsAt: new Date(prevEnd),
        endsAt: new Date(dayEnd),
      })
    }

    if (excludeAvailable || excludeUnavailable) {
      segments = segments.filter(
        ({ isAvailable }) => !(excludeAvailable && isAvailable) && !(excludeUnavailable && !isAvailable),
      )
    }

    return {
      weekday,
      segments,
      isHighlighted: !!highlightedDate && weekday === format(new Date(highlightedDate), DATE_FORMATS.WEEK_FORMAT),
    }
  })
}

export const getGroupedWeekdaysWithSegments = (weekdaysWithSegments: RawOperatingHoursBar[]) => {
  if (!weekdaysWithSegments?.length) return []

  const result: OperatingHoursBar[] = []

  let currentGroup: {
    titleParts: string[]
    segments: OperatingHoursBarSegment[] | null
    isHighlighted: boolean
  } = {
    titleParts: [],
    segments: null,
    isHighlighted: false,
  }

  for (let i = 0; i < weekdaysWithSegments.length; i++) {
    const { weekday, segments, isHighlighted } = weekdaysWithSegments[i]
    const localizedWeekday = i18n.t(`common.weekdays.${weekday.toLowerCase()}`)

    const isSameAsCurrent =
      !!currentGroup.segments &&
      !!segments &&
      isEqual(segments.map(getSegmentWithoutDates), currentGroup.segments.map(getSegmentWithoutDates))

    if (!currentGroup.segments || isSameAsCurrent) {
      currentGroup.titleParts.push(localizedWeekday)
      currentGroup.segments = segments
      currentGroup.isHighlighted ||= isHighlighted
    } else {
      result.push({
        title: currentGroup.titleParts.join(', '),
        segments: currentGroup.segments!,
        isHighlighted: currentGroup.isHighlighted,
      })

      currentGroup = {
        titleParts: [localizedWeekday],
        segments,
        isHighlighted,
      }
    }
  }

  if (currentGroup.titleParts.length) {
    result.push({
      title: currentGroup.titleParts.join(', '),
      segments: currentGroup.segments!,
      isHighlighted: currentGroup.isHighlighted,
    })
  }

  return result
}

export const formatTimeToNearestFiveMinutes = (date?: Date) =>
  date ? format(roundToNearestMinutes(new Date(date), { nearestTo: 5 }), DATE_FORMATS.TIME_FORMAT) : ''
